import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { setUserLoginStatus } from "../store/userStatus";
import { useNavigate } from "react-router-dom";
import HandleLogin from "../hooks/handleLogin";
import { useDispatch } from "react-redux";
import { setShowPreloader } from "../store/activePage";
import {
  FaEye as Eye,
  FaEyeSlash as EyeOff,
  FaLock as Lock,
  FaSpinner as Loader2,
  FaUser as User,
} from "react-icons/fa";
import { MdErrorOutline as AlertCircle } from "react-icons/md";

const validationSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  password: Yup.string().required("Password is required"),
});

const LoginScreen = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const logo = require("../assets/images/logo.jpeg");
  const dispatch = useDispatch();

  const LoginFormHandler = async (values) => {
    dispatch(setShowPreloader(true));
    setIsSubmitting(true);
    const startTime = Date.now();
    try {
      const LoginReq = await HandleLogin(values);
      const elapsed = Date.now() - startTime;
      if (elapsed < 1000) {
        await new Promise((resolve) => setTimeout(resolve, 1000 - elapsed));
      }
      if (LoginReq) {
        const token = LoginReq.access_token;
        localStorage.setItem("dwt-entry-tok", token);
        localStorage.setItem("event-info", JSON.stringify(LoginReq));
        dispatch(setUserLoginStatus(true));
        navigate("/home");
      }
    } catch (err) {
      // error is handled inside HandleLogin
    } finally {
      setIsSubmitting(false);
      dispatch(setShowPreloader(false));
    }
  };

  return (
    <div className="flex min-h-screen w-full">
      {/* ── Left Panel — Brand ───────────────────────────────────── */}
      <div
        className="hidden lg:flex lg:w-[52%] relative overflow-hidden"
        style={{ background: "linear-gradient(155deg, #1a252b 0%, #2a353c 40%, #36454f 100%)" }}
      >
        {/* Subtle dot pattern */}
        <div className="absolute inset-0 opacity-[0.06]">
          <svg className="h-full w-full" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice">
            <defs>
              <pattern id="dots" width="18" height="18" patternUnits="userSpaceOnUse">
                <circle cx="1.5" cy="1.5" r="1.5" fill="white" />
              </pattern>
            </defs>
            <rect width="200" height="200" fill="url(#dots)" />
          </svg>
        </div>

        {/* Radial glow — top right gold */}
        <div
          className="absolute -top-20 -right-20 w-[360px] h-[360px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(218,165,32,0.15) 0%, transparent 70%)" }}
        />
        {/* Radial glow — bottom left */}
        <div
          className="absolute -bottom-28 -left-28 w-[400px] h-[400px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(218,165,32,0.08) 0%, transparent 70%)" }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between w-full px-14 py-14 text-white">
          {/* Top — wordmark */}
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ background: "#daa520" }}
            />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: "rgba(218,165,32,0.7)" }}>
              Digital Moi Tech
            </span>
          </div>

          {/* Middle — hero */}
          <div className="space-y-8">
            {/* Logo */}
            <div
              className="w-24 h-24 rounded-2xl overflow-hidden flex items-center justify-center"
              style={{
                border: "2px solid rgba(218,165,32,0.5)",
                background: "rgba(255,255,255,0.05)",
                boxShadow: "0 0 32px rgba(218,165,32,0.15)",
              }}
            >
              <img src={logo} alt="Moi Note Logo" className="w-full h-full object-cover" />
            </div>

            {/* Brand name */}
            <div>
              <h1 className="text-6xl font-extrabold leading-none tracking-tight" style={{ color: "#daa520" }}>
                MOI
              </h1>
              <h1 className="text-6xl font-extrabold leading-none tracking-tight text-white -mt-1">
                NOTE
              </h1>
              <div className="flex items-center gap-3 mt-4">
                <div className="h-px w-8" style={{ background: "#daa520" }} />
                <p className="text-sm font-medium tracking-wider" style={{ color: "rgba(218,165,32,0.75)" }}>
                  EVENT CONTRIBUTION REGISTER
                </p>
              </div>
            </div>

            {/* Description */}
            <p className="text-sm leading-relaxed max-w-xs" style={{ color: "rgba(255,255,255,0.45)" }}>
              Record and manage moi contributions for your events with accuracy, speed, and full export support.
            </p>

            {/* Decorative divider */}
            <div className="flex items-center gap-4">
              <div className="h-px flex-1" style={{ background: "rgba(218,165,32,0.2)" }} />
              <svg viewBox="0 0 16 16" fill="none" className="w-4 h-4 flex-shrink-0" style={{ color: "#daa520" }}>
                <path d="M8 0l1.76 5.41H16l-4.88 3.55 1.76 5.41L8 11.41l-4.88 3.55 1.76-5.41L0 5.41h6.24z" fill="currentColor" />
              </svg>
              <div className="h-px flex-1" style={{ background: "rgba(218,165,32,0.2)" }} />
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { value: "Cash", label: "Payments" },
                { value: "Online", label: "Transfers" },
                { value: "Gifts", label: "Tracking" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-xl px-3 py-3 text-center"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <p className="text-sm font-bold" style={{ color: "#daa520" }}>{s.value}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.35)" }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <p className="text-xs tracking-wide" style={{ color: "rgba(255,255,255,0.2)" }}>
            © {new Date().getFullYear()} Digital Moi Tech · Operator Entry App
          </p>
        </div>
      </div>

      {/* ── Right Panel — Form ───────────────────────────────────── */}
      <div className="w-full lg:w-[48%] flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-10 gap-3">
            <div
              className="w-16 h-16 rounded-xl overflow-hidden"
              style={{ border: "2px solid #daa520" }}
            >
              <img src={logo} alt="Moi Note" className="w-full h-full object-cover" />
            </div>
            <span className="text-lg font-extrabold tracking-widest" style={{ color: "#daa520" }}>MOI NOTE</span>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">Welcome Back</h2>
            <p className="mt-2 text-sm text-gray-500">Sign in to continue to your event dashboard</p>
          </div>

          {/* Form */}
          <Formik
            initialValues={{ username: "", password: "" }}
            validationSchema={validationSchema}
            onSubmit={(values) => LoginFormHandler(values)}
          >
            {({ errors, touched, isSubmitting: formSubmitting }) => (
              <Form className="space-y-5">
                {/* Username */}
                <div>
                  <label htmlFor="username" className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                    Username
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User
                        className="h-4 w-4 transition-colors"
                        style={{ color: errors.username && touched.username ? "#ef4444" : "#9ca3af" }}
                      />
                    </div>
                    <Field
                      id="username"
                      name="username"
                      className="block w-full pl-11 pr-4 py-3.5 bg-white border rounded-xl text-sm text-gray-900 no-translate placeholder-gray-400 outline-none transition-all shadow-sm"
                      style={{
                        borderColor: errors.username && touched.username ? "#fca5a5" : "#e5e7eb",
                        "--tw-ring-color": "#daa520",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "#daa520"; e.target.style.boxShadow = "0 0 0 3px rgba(218,165,32,0.15)"; }}
                      onBlur={(e) => {
                        e.target.style.borderColor = errors.username && touched.username ? "#fca5a5" : "#e5e7eb";
                        e.target.style.boxShadow = "none";
                      }}
                      placeholder="Enter your username"
                    />
                  </div>
                  {errors.username && touched.username && (
                    <p className="mt-2 text-xs text-red-500 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      {errors.username}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="password" className="block text-xs font-semibold text-gray-500 uppercase tracking-widest">
                      Password
                    </label>
                    <a href="#" className="text-xs font-medium hover:opacity-80 transition-opacity" style={{ color: "#daa520" }}>
                      Forgot password?
                    </a>
                  </div>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock
                        className="h-4 w-4 transition-colors"
                        style={{ color: errors.password && touched.password ? "#ef4444" : "#9ca3af" }}
                      />
                    </div>
                    <Field
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      className="block w-full pl-11 pr-12 py-3.5 bg-white border rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none transition-all shadow-sm"
                      style={{
                        borderColor: errors.password && touched.password ? "#fca5a5" : "#e5e7eb",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "#daa520"; e.target.style.boxShadow = "0 0 0 3px rgba(218,165,32,0.15)"; }}
                      onBlur={(e) => {
                        e.target.style.borderColor = errors.password && touched.password ? "#fca5a5" : "#e5e7eb";
                        e.target.style.boxShadow = "none";
                      }}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && touched.password && (
                    <p className="mt-2 text-xs text-red-500 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Remember me */}
                <div className="flex items-center gap-2.5">
                  <input
                    id="remember-me"
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                    style={{ accentColor: "#daa520" }}
                  />
                  <label htmlFor="remember-me" className="text-sm text-gray-600 select-none cursor-pointer">
                    Remember me
                  </label>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting || formSubmitting}
                  className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    background: "linear-gradient(135deg, #c49a19 0%, #daa520 50%, #c49a19 100%)",
                    color: "#1a1a1a",
                    boxShadow: "0 4px 16px rgba(218,165,32,0.35)",
                    transform: isSubmitting || formSubmitting ? "scale(0.98)" : "scale(1)",
                  }}
                  onMouseEnter={(e) => { if (!isSubmitting && !formSubmitting) e.currentTarget.style.transform = "scale(1.02)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.98)"; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1.02)"; }}
                >
                  {isSubmitting || formSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </Form>
            )}
          </Formik>

          {/* Training Button */}
          <div className="mt-6 text-center">
            <button
              onClick={(e) => {
                e.preventDefault();
                navigate("/training");
              }}
              className="text-sm font-semibold hover:underline"
              style={{ color: "#daa520" }}
            >
              Practice Training (No Login Required)
            </button>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Powered by{" "}
              <span className="font-semibold" style={{ color: "#daa520" }}>Digital Moi Tech</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
